// frontend/src/components/SubmittedReconciliationDialog.js
import React, { useMemo } from 'react';
import {
  cashToBank as shiftCashToBank,
  exceptions as shiftExceptions,
  isFloatCountDay as floatCountDay,
  isWeeklyEntryDay as weeklyEntryDay,
  manualPaidInOut,
  variance as shiftVariance,
} from '../utils/cashTotals';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Divider,
  IconButton,
  Button,
  Stack,
  Tooltip,
  GlobalStyles,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  InfoOutlined as InfoIcon,
  WarningAmber as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

/* ========================= BRAND THEME ========================= */
const BRAND = { purple: '#65b82e', blue: '#07883f' };
const BRAND_GRADIENT = 'linear-gradient(125deg, #021f15 0%, #034f2d 58%, #07883f 100%)';
const BRAND_SOFT_BG =
  'linear-gradient(135deg, rgba(7,136,63,.10) 0%, rgba(101,184,46,.07) 58%, rgba(3,79,45,.03) 100%)';
const BRAND_PANEL_BG = (t) =>
  `linear-gradient(180deg, ${t.palette.background.paper} 0%, ${t.palette.background.default} 140%)`;

const brandGlow = {
  left: 'radial-gradient(circle at 30% 30%, rgba(7,136,63,.18), rgba(7,136,63,.04), transparent 70%)',
  right: 'radial-gradient(circle at 70% 70%, rgba(101,184,46,.15), rgba(101,184,46,.03), transparent 70%)',
};

const BrandIconBox = ({ children, sx }) => (
  <Box
    sx={{
      width: 42,
      height: 42,
      borderRadius: 2.5,
      display: 'grid',
      placeItems: 'center',
      color: 'common.white',
      boxShadow: 2,
      background: BRAND_GRADIENT,
      flex: '0 0 auto',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* ========================= SUBWAY THEME ========================= */
const SUBWAY = { green: '#008C15', yellow: '#FFC72C' };
const SUBWAY_GRADIENT = `linear-gradient(135deg, ${SUBWAY.green}, ${SUBWAY.yellow})`;
const SUBWAY_PANEL_BG = (t) =>
  `linear-gradient(180deg, ${alpha(SUBWAY.yellow, 0.1)} 0%, ${t.palette.background.paper} 38%, ${t.palette.background.default} 140%)`;

const subwayGlow = {
  left: `radial-gradient(circle at 22% 18%, ${alpha(SUBWAY.green, 0.22)}, ${alpha(
    SUBWAY.green,
    0.06
  )}, transparent 72%)`,
  right: `radial-gradient(circle at 86% 16%, ${alpha(SUBWAY.yellow, 0.18)}, ${alpha(
    SUBWAY.yellow,
    0.06
  )}, transparent 72%)`,
};

/* ===================== helpers ===================== */
const toNum = (v, def = 0) => {
  if (v === '' || v == null) return def;
  if (typeof v === 'number') return Number.isFinite(v) ? v : def;
  const s = String(v);
  const isParenNeg = s.includes('(') && s.includes(')');
  const cleaned = s.replace(/[^0-9.\-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return def;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return def;
  return isParenNeg ? -n : n;
};

const gbp = (x) => `£${toNum(x).toFixed(2)}`;

/* Counts and litres, grouped, without trailing noughts. */
const num0 = (x) => toNum(x).toLocaleString('en-GB', { maximumFractionDigits: 2 });

const iso10 = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const niceDate = (v) => {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/*
 * The arithmetic lives in utils/cashTotals.js.
 *
 * These wrappers keep the local names so every call site reads as it did,
 * but there is now one implementation of each sum rather than four that
 * drifted apart. See that file for what drifted and what it cost.
 */
const isFloatCountDay = (v) => floatCountDay(v);
const isWeeklyEntryDay = (v) => weeklyEntryDay(v);

const isSunday = (v) => {
  if (!v) return false;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return false;
  return d.getDay() === 0;
};

const pick = (obj, paths, def = undefined) => {
  for (const p of paths) {
    const parts = p.split('.');
    let cur = obj;
    let ok = true;
    for (const part of parts) {
      if (cur && typeof cur === 'object' && part in cur) cur = cur[part];
      else {
        ok = false;
        break;
      }
    }
    if (ok && cur !== undefined && cur !== null && cur !== '') return cur;
  }
  return def;
};

const isMoortownSite = (siteName, siteCode) => {
  const n = String(siteName || '').toLowerCase();
  const c = String(siteCode || '').toLowerCase();
  return n.includes('moortown') || c === 'moor' || c === 'mt' || c === 'moortown';
};

/* ===================== SOLAR EXTRACTION (FIXED) ===================== */

const extractSolar = (rec) => {
  if (!rec) return { kwh: 0 };

  /* possible solar containers */
  const solarObj =
    rec?.solar ||
    rec?.solarData ||
    rec?.solarSection ||
    rec?.energy?.solar ||
    rec?.utilities?.solar ||
    rec?.dailySummary?.solar ||
    rec?.metrics?.solar ||
    null;

  /* check every possible key used across sites */
  const kwh = toNum(
    pick(
      { rec, solarObj },
      [
        /* nested objects */
        "solarObj.kwhDay",
        "solarObj.generated",
        "solarObj.generatedKwh",
        "solarObj.generation",
        "solarObj.productionKwh",
        "solarObj.outputKwh",

        /* direct fields on reconciliation */
        "rec.solarGeneratedUnits",
        "rec.solarGenerated",
        "rec.solarGeneratedKwh",
        "rec.solarGeneration",
        "rec.solarGenerationKwh",
        "rec.solarOutput",
        "rec.solarOutputKwh",

        /* metrics */
        "rec.metrics.solar",
        "rec.metrics.solarKwh",
        "rec.metrics.solarGeneration",

        /* daily summary */
        "rec.dailySummary.solar",
        "rec.dailySummary.solarKwh",

        /* weekly overview */
        "rec.weeklyOverview.solar",
        "rec.weeklyOverview.solarKwh"
      ],
      0
    ),
    0
  );

  return { kwh };
};
/* ===================== Shop tills / shifts helpers ===================== */
const TILLS = ['till1', 'till2', 'till3'];
const TILL_LABEL = { till1: 'Till 1', till2: 'Till 2', till3: 'Till 3' };

const SHIFT_META = [
  { key: 'morning', label: 'Morning (7am–3pm)' },
  { key: 'afternoon', label: 'Afternoon (3pm–11pm)' },
  { key: 'night', label: 'Night (11pm–7am)' },
];

const ensureTillShape = (shifts = {}) => {
  const looksNew = !!(shifts?.till1 || shifts?.till2 || shifts?.till3);

  let shaped = shifts || {};
  if (!looksNew) {
    shaped = {
      till1: {
        morning: shifts?.morning || {},
        afternoon: shifts?.afternoon || {},
        night: shifts?.night || {},
      },
      till2: {
        morning: shifts?.doubleCover || {},
        afternoon: shifts?.additionalDouble || {},
        night: {},
      },
      till3: { morning: {}, afternoon: {}, night: {} },
    };
  }

  for (const t of TILLS) {
    shaped[t] = shaped[t] || {};
    for (const sm of SHIFT_META) shaped[t][sm.key] = shaped[t][sm.key] || {};
  }
  return shaped;
};

const getShiftManual = (shift) => manualPaidInOut(shift);

const exceptionsFromShift = (shift) => {
  const found = shiftExceptions(shift);

  // The dialog reads these by its own shorter names.
  return {
    nmAmt: found.nmopAmount,
    drAmt: found.driveOffAmount,
    nmQty: found.nmopQty,
    drQty: found.driveOffQty,
    total: found.total,
  };
};

/* ==========================================================================
   THE PRINTED DOCUMENT

   Everything below builds the report for paper, from the same view object
   the dialog draws on screen. It is written separately on purpose.

   Printing the interface had been the approach until now, and it produced
   what you would expect: gradient panels, tinted pill cards, rounded boxes
   and a shadow under each one, laid out for a screen and then squeezed
   onto A4 - three pages of an app photographed rather than a report.

   A banking document is a different object. It is flat, it is ruled rather
   than boxed, its figures are in columns that add up, and the eye is
   carried by type and alignment instead of colour. One accent, used for
   rules and the masthead; everything else is ink on white.
   ========================================================================== */

const DOC = {
  ink: '#10201a',
  soft: '#5a6b62',
  faint: '#8a9992',
  rule: '#d5ded8',
  accent: '#04301c',
};

const docText = {
  fontSize: 10,
  color: DOC.ink,
  lineHeight: 1.35,
};

const docNum = {
  ...docText,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const docLabel = {
  fontSize: 8,
  fontWeight: 700,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: DOC.soft,
};

/** A ruled section heading — no box, no fill. */
function DocSection({ title, note, children }) {
  return (
    <section style={{ marginTop: 14, breakInside: 'avoid' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${DOC.accent}`,
          paddingBottom: 3,
          marginBottom: 6,
        }}
      >
        <span style={{ ...docLabel, color: DOC.accent, fontSize: 9 }}>{title}</span>
        {note ? <span style={{ ...docText, fontSize: 8.5, color: DOC.faint }}>{note}</span> : null}
      </div>
      {children}
    </section>
  );
}

/** label : value, on one ruled line. */
function DocRow({ label, value, strong, top }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '3px 0',
        borderTop: top ? `1.5px solid ${DOC.ink}` : `1px solid ${DOC.rule}`,
      }}
    >
      <span style={{ ...docText, fontWeight: strong ? 800 : 400 }}>{label}</span>
      <span style={{ ...docNum, fontWeight: strong ? 800 : 400 }}>{value}</span>
    </div>
  );
}

/**
 * The report, for paper.
 */
function PrintReport({ view, totals }) {
  const m = view.metrics || {};
  const variance = view.shopVariance;
  const within = Math.abs(variance) <= 15;

  const facts = [
    ['Wastage', gbp(m.wastage)],
    ['Markdown', gbp(m.markdown)],
    ['Daily float', gbp(m.dailyFloat)],
    ['Weekly / backup float', gbp(m.sundayFloat)],
    ['Costa cups dispensed', num0(m.costaDispensed)],
    ['Costa cups sold', num0(m.costaSold)],
    ['Total customers', num0(m.totalCustomers)],
    ['Fuel transactions', num0(m.fuelTransactions)],
    ['Night shift fuel', `${num0(m.nightFuel)} L`],
    ['Night shift shop sales', gbp(m.nightShopSales)],
    ['Night shift customers', num0(m.nightCustomers)],
    ['Solar generated', `${num0(m.solarKwh)} kWh`],
  ];

  return (
    <div className="print-only print-doc" style={{ color: DOC.ink }}>
      {/* Masthead */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          borderBottom: `2px solid ${DOC.accent}`,
          paddingBottom: 6,
        }}
      >
        <div>
          <div style={{ ...docLabel, color: DOC.accent }}>Valli Forecourts</div>
          <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.015em', marginTop: 2 }}>
            Daily Banking &amp; Reconciliation
          </div>
        </div>

        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Site', view.siteName + (view.siteCode ? ` (${view.siteCode})` : '')],
              ['Business date', niceDate(view.dateObj)],
              ['Status', String(view.status || '').toUpperCase()],
              ['Submitted by', view.submittedBy],
            ].map(([label, value]) => (
              <tr key={label}>
                <td style={{ ...docLabel, paddingRight: 10, textAlign: 'right' }}>{label}</td>
                <td style={{ ...docText, fontWeight: 700 }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The three figures the page exists for, as type rather than tiles. */}
      <div style={{ display: 'flex', gap: 0, marginTop: 12 }}>
        {[
          ['Cash variance', gbp(variance), within ? 'Within control' : 'Outside the £15 threshold'],
          ['Total cash to bank', gbp(view.cashToBank), 'Pouch, safe drop and paid in/out'],
          ['Expected cash', gbp(totals.totalCash), 'All shop tills'],
        ].map(([label, value, note], index) => (
          <div
            key={label}
            style={{
              flex: 1,
              paddingLeft: index ? 14 : 0,
              paddingRight: 14,
              borderLeft: index ? `1px solid ${DOC.rule}` : 'none',
            }}
          >
            <div style={docLabel}>{label}</div>
            <div
              style={{
                fontSize: 21,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
                marginTop: 1,
              }}
            >
              {value}
            </div>
            <div style={{ ...docText, fontSize: 8.5, color: DOC.faint }}>{note}</div>
          </div>
        ))}
      </div>

      <DocSection title="Cash position" note="Shop tills only">
        <DocRow label="Expected cash" value={gbp(totals.totalCash)} />
        <DocRow label="Pouch amounts" value={gbp(totals.totalPouch)} />
        <DocRow label="Safe drop / submitted cash" value={gbp(totals.totalSafeDrop)} />
        <DocRow label="Manual paid in / out" value={gbp(totals.totalManual)} />
        {view.isWeeklyEntryDay ? (
          <DocRow label="Forecourt machines cash (week)" value={gbp(view.jetwash)} />
        ) : null}
        <DocRow label="Total cash to bank" value={gbp(view.cashToBank)} strong top />
        <DocRow
          label={`Variance ${within ? '— within control' : '— requires sign-off'}`}
          value={gbp(variance)}
          strong
        />
        <div style={{ ...docText, fontSize: 8.5, color: DOC.faint, paddingTop: 4 }}>
          Exceptions {gbp(totals.totalEx)} · drive-offs {gbp(totals.totalDriveOff)} (
          {num0(totals.totalDriveOffQty)}) · NMOP {gbp(totals.totalNmop)} (
          {num0(totals.totalNmopQty)}). The paid in / out is a cash movement and is not counted
          in the variance.
        </div>
      </DocSection>

      <DocSection title="Tills and shifts">
        <PrintTillTable
          perShift={view.perShift}
          tillRollup={view.tillRollup}
          totals={totals}
          variance={variance}
        />
      </DocSection>

      <DocSection title="Day summary">
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {facts.map(([label, value]) => (
            <div key={label} style={{ width: '33.333%', paddingRight: 14, boxSizing: 'border-box' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '2.5px 0',
                  borderBottom: `1px solid ${DOC.rule}`,
                }}
              >
                <span style={{ ...docText, color: DOC.soft }}>{label}</span>
                <span style={{ ...docNum, fontWeight: 700 }}>{value}</span>
              </div>
            </div>
          ))}
        </div>
      </DocSection>

      {/* The line that makes it a record rather than a printout. */}
      <div style={{ display: 'flex', gap: 28, marginTop: 18, breakInside: 'avoid' }}>
        {[
          ['Submitted by', view.submittedBy],
          ['Checked by', ''],
          ['Date', ''],
        ].map(([label, value]) => (
          <div key={label} style={{ flex: 1 }}>
            <div
              style={{
                borderBottom: `1px solid ${DOC.ink}`,
                minHeight: 20,
                ...docText,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'flex-end',
                paddingBottom: 2,
              }}
            >
              {value}
            </div>
            <div style={{ ...docLabel, marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The till detail as a table, for print only.
 *
 * On screen the shifts are twelve cards, which is the right shape for a
 * dialog you scroll. On paper they were twelve full-width cards - the md
 * breakpoint never applies at A4 width - so six fields sat stretched
 * across the page, twelve times, over two and a half sheets with half of
 * each one empty.
 *
 * A day's banking is a table: one row per shift, one figure per column,
 * subtotals per till and a total that ties to the headline at the top.
 * That is also what makes it read as a financial document rather than a
 * screenshot of an app.
 */
function PrintTillTable({ perShift, tillRollup, totals, variance }) {
  const cell = {
    padding: '3px 6px',
    borderBottom: '1px solid #e4eae6',
    fontSize: 10,
    whiteSpace: 'nowrap',
  };

  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const head = { ...cell, fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5a6b62' };

  const money = (value) => gbp(value);

  const rowsForTill = (tillKey, label) => {
    const shifts = perShift.filter((s) => s.till === tillKey);
    if (!shifts.length) return null;

    const roll = tillRollup[tillKey] || {};

    return (
      <React.Fragment key={tillKey}>
        {shifts.map((s, index) => (
          <tr key={`${tillKey}-${index}`}>
            <td style={cell}>{s.label.replace(/^Till \d+ — /, '')}</td>
            <td style={{ ...cell, color: '#5a6b62' }}>{s.staff}</td>
            <td style={num}>{money(s.cash)}</td>
            <td style={num}>{money(s.pouch)}</td>
            <td style={num}>{money(s.safe)}</td>
            <td style={num}>{money(s.manual)}</td>
            <td style={num}>{money(s.exTotal)}</td>
            <td style={{ ...num, fontWeight: 700 }}>{money(s.variance)}</td>
          </tr>
        ))}

        <tr>
          <td style={{ ...cell, fontWeight: 800 }} colSpan={2}>
            {label} total
          </td>
          <td style={{ ...num, fontWeight: 800 }}>{money(roll.cash)}</td>
          <td style={{ ...num, fontWeight: 800 }}>{money(roll.pouch)}</td>
          <td style={{ ...num, fontWeight: 800 }}>{money(roll.safe)}</td>
          <td style={{ ...num, fontWeight: 800 }}>{money(roll.manual)}</td>
          <td style={{ ...num, fontWeight: 800 }}>{money(roll.ex)}</td>
          <td style={{ ...num, fontWeight: 800 }}>{money(roll.var)}</td>
        </tr>
      </React.Fragment>
    );
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
      <thead>
        <tr>
          <th style={{ ...head, textAlign: 'left' }}>Shift</th>
          <th style={{ ...head, textAlign: 'left' }}>Staff</th>
          <th style={{ ...head, textAlign: 'right' }}>Expected</th>
          <th style={{ ...head, textAlign: 'right' }}>Pouch</th>
          <th style={{ ...head, textAlign: 'right' }}>Safe drop</th>
          <th style={{ ...head, textAlign: 'right' }}>Paid in/out</th>
          <th style={{ ...head, textAlign: 'right' }}>Exceptions</th>
          <th style={{ ...head, textAlign: 'right' }}>Variance</th>
        </tr>
      </thead>

      <tbody>
        {rowsForTill('till1', 'Till 1')}
        {rowsForTill('till2', 'Till 2')}
        {rowsForTill('till3', 'Till 3')}

        <tr>
          <td style={{ ...cell, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }} colSpan={2}>
            All tills
          </td>
          <td style={{ ...num, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }}>{money(totals.totalCash)}</td>
          <td style={{ ...num, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }}>{money(totals.totalPouch)}</td>
          <td style={{ ...num, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }}>{money(totals.totalSafeDrop)}</td>
          <td style={{ ...num, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }}>{money(totals.totalManual)}</td>
          <td style={{ ...num, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }}>{money(totals.totalEx)}</td>
          <td style={{ ...num, fontWeight: 900, borderTop: '2px solid #04301c', borderBottom: 'none' }}>{money(variance)}</td>
        </tr>
      </tbody>
    </table>
  );
}

/*
 * Shift-level display variance.
 *
 * Without the manual paid in/out, which is a cash movement rather than
 * cash gone astray. This still counted it, so the tills on this report
 * added up to £24.91 while the headline at the top of the same page said
 * £4.91 - the £20 paid in, counted twice over.
 */
const varianceFromShift = (shift) => shiftVariance(shift);

const staffSummaryFromShift = (shift) => {
  const raw =
    (Array.isArray(shift?.staffOnShiftNames) && shift.staffOnShiftNames) ||
    (Array.isArray(shift?.cashiers) && shift.cashiers) ||
    (Array.isArray(shift?.staffOnShift) && shift.staffOnShift) ||
    [];

  if (!raw.length) return 'No staff recorded';

  const names = raw
    .map((p) => {
      if (!p) return '';
      if (typeof p === 'string') {
        const trimmed = p.trim();
        if (!trimmed) return '';
        if (/^[0-9a-fA-F]{24}$/.test(trimmed)) return '';
        return trimmed;
      }
      const full = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
      return p.displayName || p.fullName || p.name || p.staffName || full || p.email || '';
    })
    .filter(Boolean);

  if (!names.length) return `${raw.length} staff on duty`;
  return names.join(', ');
};

/* ===================== exact shop day variance ===================== */
/* Shop only. Subway excluded. This is the actual total shop cash loss/up of the day. */
const calculateShopDayVariance = (rec) => {
  const shaped = ensureTillShape(rec?.shifts || {});

  let expectedCash = 0;
  let pouchAmounts = 0;
  let safeDrop = 0;
  let manualTotal = 0;
  let driveOffAmount = 0;
  let nmopAmount = 0;
  let driveOffQty = 0;
  let nmopQty = 0;

  for (const till of TILLS) {
    for (const sm of SHIFT_META) {
      const s = shaped?.[till]?.[sm.key] || {};
      const ex = exceptionsFromShift(s);

      expectedCash += toNum(s?.totalCashValue ?? s?.totalCash, 0);
      pouchAmounts += toNum(s?.pouchAmounts ?? s?.pouchAmount, 0);
      safeDrop += toNum(s?.submittedCash ?? s?.safeDrop, 0);
      manualTotal += getShiftManual(s);
      driveOffAmount += ex.drAmt;
      nmopAmount += ex.nmAmt;
      driveOffQty += ex.drQty;
      nmopQty += ex.nmQty;
    }
  }

  const actualCashMoved = pouchAmounts + safeDrop;

  /* Cash-only difference */
  const cashVarianceOnly = actualCashMoved - expectedCash + driveOffAmount + nmopAmount;

  /* Final day shop loss/up */
  const totalCashLossUp = cashVarianceOnly + manualTotal;

  return {
    expectedCash,
    pouchAmounts,
    safeDrop,
    manualTotal,
    driveOffAmount,
    nmopAmount,
    driveOffQty,
    nmopQty,
    exceptionsTotal: driveOffAmount + nmopAmount,
    cashVarianceOnly,
    totalCashLossUp,
  };
};

const StatusChip = ({ status }) => {
  const s = (status || '').toUpperCase();
  let color = 'default';
  if (s === 'SUBMITTED' || s === 'APPROVED') color = 'success';
  else if (s === 'REJECTED') color = 'error';
  else if (s === 'DRAFT') color = 'warning';

  return (
    <Chip
      size="small"
      label={s || '—'}
      color={color}
      variant={s === 'APPROVED' ? 'filled' : 'outlined'}
      sx={{ fontWeight: 900, borderRadius: 999 }}
    />
  );
};

const VariancePill = ({ value }) => {
  const v = toNum(value);
  const abs = Math.abs(v);
  const isZero = abs < 0.01;
  const isBig = abs >= 100;
  const color = isZero ? 'default' : v > 0 ? 'success' : 'error';

  return (
    <Chip
      size="small"
      color={isBig ? 'warning' : color}
      icon={isZero ? <CheckCircleIcon /> : isBig ? <WarningIcon /> : undefined}
      label={`${v >= 0 ? '+' : ''}${gbp(abs)}`}
      sx={{ fontWeight: 900, borderRadius: 999, px: 0.5, '& .MuiChip-label': { letterSpacing: 0.2 } }}
      variant={isZero ? 'outlined' : 'filled'}
    />
  );
};

const SummaryKpi = ({ label, value, hint, accent = false, subway = false }) => (
  <Paper
    variant="outlined"
    className="avoid-break"
    sx={{
      p: 1.5,
      borderRadius: 3,
      height: '100%',
      background: subway ? SUBWAY_PANEL_BG : accent ? BRAND_SOFT_BG : 'background.paper',
      position: 'relative',
      overflow: 'hidden',
      borderColor: subway ? alpha(SUBWAY.green, 0.16) : 'divider',
    }}
  >
    {accent && !subway ? (
      <>
        <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.left, opacity: 0.45, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.right, opacity: 0.45, pointerEvents: 'none' }} />
      </>
    ) : null}

    {subway ? (
      <>
        <Box sx={{ position: 'absolute', inset: -2, background: subwayGlow.left, opacity: 0.16, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: -2, background: subwayGlow.right, opacity: 0.16, pointerEvents: 'none' }} />
      </>
    ) : null}

    <Box sx={{ position: 'relative' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 1000, lineHeight: 1.15 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

function Field({ label, value, emphasize = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          textAlign: 'right',
          fontWeight: emphasize ? 1000 : 900,
          color: emphasize ? 'warning.main' : 'text.primary',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ===================== subway extractor ===================== */
const extractSubwayForReport = (rec) => {
  if (!rec) return null;

  const root =
    pick(rec, ['subway', 'subwaySection', 'subwayReconciliation', 'subwayData', 'subwayPack'], null) ||
    null;
  if (!root) return null;

  const metrics = root?.metrics || {};
  const extra = root?.extra || {};
  const extraExtra = extra?.extra || {};
  const shiftsBag = rec?.shifts || {};

  const subwayShift =
    shiftsBag?.subway?.day ||
    shiftsBag?.subway?.single ||
    shiftsBag?.subway?.main ||
    shiftsBag?.subway ||
    root?.shift ||
    root?.singleShift ||
    {};

  const floatAmount = toNum(
    pick(
      { root, metrics, extra, extraExtra, subwayShift },
      [
        'extraExtra.floatAmount',
        'extraExtra.subwayFloat',
        'extraExtra.float',
        'extra.floatAmount',
        'extra.subwayFloat',
        'root.floatAmount',
        'root.subwayFloat',
        'root.float',
        'metrics.floatAmount',
      ],
      150
    ),
    150
  );

  const totalCashBeforeFloat = toNum(
    pick(
      { root, metrics, extra, extraExtra, subwayShift },
      [
        'extraExtra.totalCashValue',
        'extraExtra.totalCash',
        'extra.totalCashValue',
        'extra.totalCash',
        'root.totalCashValue',
        'root.totalCash',
        'subwayShift.totalCashValue',
        'subwayShift.totalCash',
      ],
      0
    ),
    0
  );

  const pouchTotal = toNum(
    pick(
      { root, metrics, extra, extraExtra, subwayShift },
      [
        'extraExtra.pouchAmounts',
        'extraExtra.pouchAmount',
        'extra.pouchAmounts',
        'extra.pouchAmount',
        'root.pouchAmounts',
        'root.pouchAmount',
        'subwayShift.pouchAmounts',
        'subwayShift.pouchAmount',
      ],
      0
    ),
    0
  );

  const safeDropTotal = toNum(
    pick(
      { root, metrics, extra, extraExtra, subwayShift },
      [
        'extraExtra.submittedCash',
        'extraExtra.cashSafeDrop',
        'extraExtra.safeDrop',
        'extra.submittedCash',
        'extra.cashSafeDrop',
        'extra.safeDrop',
        'root.submittedCash',
        'root.cashSafeDrop',
        'root.safeDrop',
        'subwayShift.submittedCash',
        'subwayShift.cashSafeDrop',
        'subwayShift.safeDrop',
      ],
      0
    ),
    0
  );

  const manualTotal = toNum(
    pick(
      { root, metrics, extra, extraExtra, subwayShift },
      [
        'extraExtra.manualPaidInOut',
        'extraExtra.manualPaidInOutValue',
        'extra.manualPaidInOut',
        'extra.manualPaidInOutValue',
        'root.manualPaidInOut',
        'root.manualPaidInOutValue',
        'subwayShift.manualPaidInOut',
        'subwayShift.manualPaidInOutValue',
      ],
      0
    ),
    0
  );

  const totalSales = toNum(
    pick(
      { root, metrics, extra, extraExtra },
      [
        'metrics.totalSalesDay',
        'metrics.totalSales',
        'root.totalSalesDay',
        'root.totalSales',
        'extraExtra.totalSalesDay',
        'extra.totalSalesDay',
      ],
      0
    ),
    0
  );

  const totalCustomers = toNum(
    pick(
      { root, metrics, extra, extraExtra },
      [
        'metrics.totalCustomersDay',
        'metrics.totalCustomers',
        'root.totalCustomersDay',
        'root.totalCustomers',
        'extraExtra.totalCustomersDay',
      ],
      0
    ),
    0
  );

  const nightSales = toNum(
    pick({ root, metrics, extra, extraExtra }, ['metrics.nightSales', 'root.nightSales'], 0),
    0
  );

  const nightCustomers = toNum(
    pick({ root, metrics, extra, extraExtra }, ['metrics.nightCustomers', 'root.nightCustomers'], 0),
    0
  );

  const weeklyWastage = toNum(
    pick(
      { root, metrics, extra, extraExtra },
      [
        'extraExtra.weeklyWastage',
        'metrics.weeklyWastage',
        'root.weeklyWastage',
        'extra.weeklyWastage',
      ],
      0
    ),
    0
  );

  const totalCashAfterFloat = Math.max(0, totalCashBeforeFloat - floatAmount);

  const explicitVariance = toNum(
    pick(
      { root, metrics, extra, extraExtra, subwayShift },
      [
        'extraExtra.cashVariance',
        'metrics.cashVariance',
        'root.cashVariance',
        'subwayShift.cashVariance',
        'root.variance',
        'subwayShift.variance',
      ],
      0
    ),
    0
  );

  const computedVariance = pouchTotal + safeDropTotal + manualTotal - totalCashAfterFloat;
  const cashVariance = Math.abs(explicitVariance) > 0.0001 ? explicitVariance : computedVariance;

  const extractStaffName = (staffId, allShifts) => {
    if (!staffId || !allShifts) return null;

    for (const till of ['till1', 'till2', 'till3', 'subway']) {
      const tillData = allShifts[till];
      if (!tillData) continue;

      for (const shiftName of ['morning', 'afternoon', 'night', 'day', 'single', 'main']) {
        const shiftData = tillData?.[shiftName];
        if (!shiftData?.cashiers || !Array.isArray(shiftData.cashiers)) continue;

        const found = shiftData.cashiers.find((c) => c && (c._id === staffId || c.id === staffId));
        if (found) {
          return (
            [found.firstName, found.lastName].filter(Boolean).join(' ') ||
            found.fullName ||
            found.name ||
            found.email ||
            null
          );
        }
      }
    }

    return null;
  };

  const staffRaw =
    pick({ root, extra, extraExtra, subwayShift }, [
      'extraExtra.staffOnDuty',
      'extra.staffOnDuty',
      'root.staffOnDuty',
      'subwayShift.staffOnDuty',
      'subwayShift.staffOnShiftNames',
      'subwayShift.cashiers',
    ]) || [];

  let staffText = 'No staff recorded';

  if (Array.isArray(staffRaw) && staffRaw.length) {
    const names = staffRaw
      .map((item) => {
        if (!item) return '';

        if (typeof item === 'string') {
          const resolved = extractStaffName(item, shiftsBag);
          if (resolved) return resolved;
          if (/^[0-9a-fA-F]{24}$/.test(item)) return '';
          return item.trim();
        }

        const full = [item.firstName, item.lastName].filter(Boolean).join(' ').trim();
        return item.displayName || item.fullName || item.name || item.staffName || full || item.email || '';
      })
      .filter(Boolean);

    if (names.length) staffText = names.join(', ');
  } else if (typeof staffRaw === 'string' && staffRaw.trim()) {
    const resolved = extractStaffName(staffRaw.trim(), shiftsBag);
    staffText = resolved || staffRaw.trim();
  }

  return {
    totals: {
      totalSales,
      totalCustomers,
      nightSales,
      nightCustomers,
      weeklyWastage,
      cashVariance,
      totalCashBeforeFloat,
      floatAmount,
      totalCashAfterFloat,
      pouchTotal,
      safeDropTotal,
      manualTotal,
    },
    shift: {
      label: 'Subway day shift (single shift)',
      staffText,
      pouch: pouchTotal,
      safeDrop: safeDropTotal,
      manual: manualTotal,
    },
  };
};

/* ===================== component ===================== */
export default function SubmittedReconciliationDialog({ open, onClose, reconciliation }) {
  const theme = useTheme();
  const rec = reconciliation;

  const view = useMemo(() => {
    if (!rec) return null;

    const dateRaw =
      rec.forDate ||
      rec.businessDate ||
      rec.date ||
      rec.meta?.forDate ||
      rec.meta?.date ||
      rec.createdAt ||
      null;

    const dateObj = dateRaw ? new Date(dateRaw) : null;

    const siteName = rec.site?.name || rec.siteName || rec.site?.code || rec.siteCode || 'Site';
    const siteCode = rec.site?.code || rec.siteCode || '';

    const submittedBy =
      rec.submittedByName ||
      rec.submittedBy?.fullName ||
      rec.submittedBy?.email ||
      rec.submittedBy ||
      rec.user?.name ||
      rec.userName ||
      '—';

    const shifts = ensureTillShape(rec.shifts || {});
    const shopDay = calculateShopDayVariance(rec);
    const solar = extractSolar(rec);

    const perShift = [];
    const tillRollup = {
      till1: { cash: 0, pouch: 0, safe: 0, ex: 0, nm: 0, dr: 0, nmQty: 0, drQty: 0, var: 0, manual: 0 },
      till2: { cash: 0, pouch: 0, safe: 0, ex: 0, nm: 0, dr: 0, nmQty: 0, drQty: 0, var: 0, manual: 0 },
      till3: { cash: 0, pouch: 0, safe: 0, ex: 0, nm: 0, dr: 0, nmQty: 0, drQty: 0, var: 0, manual: 0 },
    };

    for (const t of TILLS) {
      for (const sm of SHIFT_META) {
        const sh = shifts?.[t]?.[sm.key] || {};
        const cash = toNum(sh.totalCashValue ?? sh.totalCash);
        const pouch = toNum(sh.pouchAmounts ?? sh.pouchAmount);
        const safe = toNum(sh.submittedCash ?? sh.safeDrop);
        const manual = getShiftManual(sh);
        const ex = exceptionsFromShift(sh);
        const variance = varianceFromShift(sh);

        tillRollup[t].cash += cash;
        tillRollup[t].pouch += pouch;
        tillRollup[t].safe += safe;
        tillRollup[t].manual += manual;
        tillRollup[t].ex += ex.total;
        tillRollup[t].nm += ex.nmAmt;
        tillRollup[t].dr += ex.drAmt;
        tillRollup[t].nmQty += ex.nmQty;
        tillRollup[t].drQty += ex.drQty;
        tillRollup[t].var += variance;

        perShift.push({
          till: t,
          shift: sm.key,
          label: `${TILL_LABEL[t]} — ${sm.label}`,
          cash,
          pouch,
          safe,
          manual,
          exTotal: ex.total,
          nmAmt: ex.nmAmt,
          drAmt: ex.drAmt,
          nmQty: ex.nmQty,
          drQty: ex.drQty,
          variance,
          staff: staffSummaryFromShift(sh),
        });
      }
    }

    const wastage = toNum(rec.wastage?.value ?? rec.wastage?.amount ?? rec.wastage?.total);
    const markdown = toNum(rec.markdown?.value ?? rec.markdown?.amount ?? rec.markdown?.total);
    const dailyFloat = toNum(rec.floats?.dailyFloat ?? rec.bottomFloat ?? rec.shopFloat);
    const sundayFloat = toNum(rec.backupFloat);

    const costaDispensed = toNum(rec.costaMachine?.cupsDispensed ?? rec.costa?.cupsDispensed);
    const costaSold = toNum(rec.costaMachine?.cupsSold ?? rec.costa?.cupsSold);

    const totalCustomers = toNum(rec.customerMetrics?.totalCustomers);
    const fuelTransactions = toNum(rec.customerMetrics?.fuelTransactions);
    const fuelOnlyPct = totalCustomers > 0 ? (fuelTransactions / totalCustomers) * 100 : 0;

    const nightFuel = toNum(
      rec.nightMetrics?.fuelLitres ??
        rec.nightShiftBreakdown?.fuelSalesLitres ??
        rec.nightBreakdown?.fuelSalesLitres ??
        rec.shifts?.night?.fuelLitres ??
        rec.shifts?.night?.fuelSalesLitres
    );

    const nightShopSales = toNum(
      rec.nightMetrics?.shopSales ??
        rec.nightShiftBreakdown?.shopSales ??
        rec.nightBreakdown?.shopSales ??
        rec.shifts?.night?.shopSales
    );

    const nightCustomers = toNum(
      rec.nightMetrics?.customers ??
        rec.nightShiftBreakdown?.customers ??
        rec.nightBreakdown?.customers ??
        rec.shifts?.night?.customers ??
        rec.shifts?.night?.customerCount ??
        rec.shifts?.night?.count
    );

    const isMoortown = isMoortownSite(siteName, siteCode);
    const subway = isMoortown ? extractSubwayForReport(rec) : null;

    /*
     * The jetwash cash and the total that goes to the bank.
     *
     * Neither was on this report. It listed what was expected, what was in
     * the pouch, what was safe-dropped and what was paid in or out, and
     * then stopped one addition short of the figure the whole page is
     * about - while the jetwash money, which is banked with the rest, was
     * not mentioned at all.
     *
     * Built from the same parts as the site's own form rather than read
     * from the stored total, so an older record cannot show a figure that
     * disagrees with the lines printed above it.
     */
    /*
     * Each machine is entered as card and cash now; only the cash is banked.
     * Older records carried one figure under the jetwash's name, so the cash
     * falls back to that single field when the split is absent.
     */
    const machineCash = (cashKey, legacyKey) =>
      toNum(pick(rec, [cashKey], 0), 0) || toNum(pick(rec, [legacyKey], 0), 0);
    const jetwash = isWeeklyEntryDay(dateObj || dateRaw)
      ? machineCash('jetwashCashAmount', 'jetwashReconciliationAmount') +
        machineCash('airWaterCashAmount', 'airWaterAmount') +
        machineCash('vacuumCashAmount', 'vacuumAmount')
      : 0;

    const cashToBank =
      toNum(shopDay.pouchAmounts) +
      toNum(shopDay.safeDrop) +
      toNum(shopDay.manualTotal) +
      (subway
        ? toNum(subway.totals.pouchTotal) +
          toNum(subway.totals.safeDropTotal) +
          toNum(subway.totals.manualTotal)
        : 0) +
      jetwash;

    return {
      dateObj: dateObj || dateRaw,
      siteName,
      siteCode,
      submittedBy,
      status: rec.status,
      isSunday: isSunday(dateObj || dateRaw),
      isFloatCountDay: isFloatCountDay(dateObj || dateRaw),
      isWeeklyEntryDay: isWeeklyEntryDay(dateObj || dateRaw),
      jetwash,
      cashToBank,
      // The variance is what is unaccounted for; the manual paid in/out
      // is accounted for by definition, so it is not part of it.
      shopVariance: shopDay.cashVarianceOnly,
      totals: {
        totalCash: shopDay.expectedCash,
        totalPouch: shopDay.pouchAmounts,
        totalSafeDrop: shopDay.safeDrop,
        totalManual: shopDay.manualTotal,
        totalEx: shopDay.exceptionsTotal,
        totalNmop: shopDay.nmopAmount,
        totalDriveOff: shopDay.driveOffAmount,
        totalNmopQty: shopDay.nmopQty,
        totalDriveOffQty: shopDay.driveOffQty,
      },
      perShift,
      tillRollup,
      metrics: {
        wastage,
        markdown,
        dailyFloat,
        sundayFloat,
        costaDispensed,
        costaSold,
        totalCustomers,
        fuelTransactions,
        fuelOnlyPct,
        nightFuel,
        nightShopSales,
        nightCustomers,
        solarKwh: solar.kwh,
      },
      isMoortown,
      subway,
    };
  }, [rec]);

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  if (!rec || !view) return null;

  const { totals, metrics, tillRollup } = view;
  const varianceAbs = Math.abs(toNum(view.shopVariance));
  const varianceSeverity = varianceAbs >= 100 ? 'warning' : varianceAbs >= 0.01 ? 'info' : 'success';
  const cashAccounted = totals.totalCash
    ? ((totals.totalPouch + totals.totalSafeDrop) / totals.totalCash) * 100
    : 0;
  const costaVariance = Number(metrics.costaSold || 0) - Number(metrics.costaDispensed || 0);

  const cardSx = { borderRadius: 3, borderColor: 'divider', overflow: 'hidden' };
  const chipSx = {
    height: 28,
    borderRadius: 999,
    fontWeight: 900,
    '& .MuiChip-label': { px: 1.1, lineHeight: 1 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          background: `radial-gradient(1200px 450px at 15% 0%, ${alpha(BRAND.blue, 0.12)} 0%, rgba(0,0,0,0) 55%),
                       radial-gradient(900px 420px at 85% 10%, ${alpha(BRAND.purple, 0.12)} 0%, rgba(0,0,0,0) 55%),
                       ${theme.palette.background.default}`,
        },
      }}
    >
      <GlobalStyles
        styles={{
          '.print-only': { display: 'none' },

          '@media print': {
            '@page': { margin: '12mm 10mm' },
            'html, body': {
              height: 'auto',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
            },
            '.no-print': { display: 'none !important' },
            '.print-tip': { display: 'none !important' },
            '.keep-with-next': { breakAfter: 'avoid-page', pageBreakAfter: 'avoid' },
            '.avoid-break': { breakInside: 'avoid', pageBreakInside: 'avoid' },
            '.print-page': { paddingTop: '0 !important' },

            /* The interface does not print; the document does. */
            '.screen-only': { display: 'none !important' },
            '.print-only': { display: 'block !important' },

            '.MuiDialog-paper, .MuiDialogContent-root': {
              padding: '0 !important',
              margin: '0 !important',
              boxShadow: 'none !important',
              background: '#ffffff !important',
              overflow: 'visible !important',
              maxWidth: 'none !important',
            },

            /* The dialog paints a tinted gradient behind everything. A
               report is ink on white. */
            '.MuiDialog-container, .MuiBackdrop-root': { background: '#ffffff !important' },

            '.print-sheet': {
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              color: '#10201a',
              padding: '0 0 14mm',
            },

            /* A long table pages properly and takes its headings with it. */
            thead: { display: 'table-header-group' },
            tr: { breakInside: 'avoid' },

            /* The one piece of furniture a printed report needs and the
               browser's own header cannot give it: what this document is,
               on every sheet. */
            '.print-footer': {
              display: 'block !important',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              paddingTop: '3mm',
              borderTop: '1px solid #dce5df',
              color: '#5a6b62',
              fontSize: 9,
              letterSpacing: '0.03em',
            },
          },
        }}
      />

      <Box
        className="no-print"
        sx={{
          px: { xs: 1.5, sm: 2.25 },
          pt: { xs: 1.5, sm: 2 },
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            ...cardSx,
            position: 'relative',
            p: { xs: 1.8, sm: 2.25 },
            color: 'common.white',
            background: BRAND_GRADIENT,
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0, background: brandGlow.left, opacity: 0.95, pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', inset: 0, background: brandGlow.right, opacity: 0.95, pointerEvents: 'none' }} />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1.6, md: 2 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ position: 'relative' }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <BrandIconBox sx={{ width: 46, height: 46 }}>
                <ReceiptLongIcon />
              </BrandIconBox>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" sx={{ letterSpacing: 1, opacity: 0.92, lineHeight: 1.1 }}>
                  RECONCILIATION REPORT
                </Typography>

                <Typography sx={{ fontWeight: 1000, lineHeight: 1.1, fontSize: { xs: 18, sm: 22 } }} noWrap>
                  {view.siteName}
                </Typography>

                <Stack direction="row" spacing={0.8} flexWrap="wrap" rowGap={0.8} sx={{ mt: 0.8 }}>
                  <Chip
                    size="small"
                    label={niceDate(view.dateObj)}
                    sx={{
                      ...chipSx,
                      color: 'common.white',
                      borderColor: 'rgba(255,255,255,0.35)',
                      bgcolor: 'rgba(255,255,255,0.14)',
                    }}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`By: ${view.submittedBy}`}
                    variant="outlined"
                    sx={{
                      ...chipSx,
                      color: 'common.white',
                      borderColor: 'rgba(255,255,255,0.35)',
                      bgcolor: 'rgba(255,255,255,0.10)',
                    }}
                  />
                  <StatusChip status={view.status} />
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Tooltip title="Print / Save as PDF">
                <Button
                  onClick={handlePrint}
                  variant="contained"
                  startIcon={<PrintIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    px: 2,
                    background: 'rgba(255,255,255,0.18)',
                    boxShadow: 2,
                    '&:hover': { background: 'rgba(255,255,255,0.22)' },
                  }}
                >
                  Print
                </Button>
              </Tooltip>

              <Tooltip title="Close">
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: 'common.white',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      <DialogTitle className="no-print" sx={{ display: 'none' }} />
      <DialogContent sx={{ pt: 0 }}>
        {/*
          The interface, for the screen. It does not print: the document
          below is built for paper instead. Photographing an app was what
          produced three pages of gradient panels and pill cards.
        */}
        <Box
          className="print-page screen-only"
          sx={{
            pt: { xs: 2, sm: 2.5 },
            pb: 4,
            px: { xs: 1.5, sm: 2.25, md: 3 },
            maxWidth: 1400,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Top summary */}
          <Paper
            elevation={0}
            variant="outlined"
            className="avoid-break"
            sx={{
              ...cardSx,
              position: 'relative',
              p: { xs: 2, sm: 2.25 },
              background: (t) => BRAND_PANEL_BG(t),
            }}
          >
            <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.left, opacity: 0.45, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.right, opacity: 0.45, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', left: 0, top: 0, right: 0, height: 4, background: BRAND_GRADIENT }} />

            <Grid container spacing={2} alignItems="stretch" sx={{ position: 'relative' }}>
              <Grid item xs={12} md={7}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                  Valli Forecourts · Smart Banking & Reconciliation
                </Typography>

                <Typography variant="h5" sx={{ fontWeight: 1000, lineHeight: 1.12, mt: 0.25 }}>
                  Daily Banking & Reconciliation
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.9} flexWrap="wrap" rowGap={0.9} sx={{ mt: 1.1 }}>
                  <Chip label={view.siteName} sx={{ ...chipSx, fontWeight: 1000 }} />
                  <Chip label={niceDate(view.dateObj)} variant="outlined" sx={chipSx} />
                  <StatusChip status={view.status} />
                </Stack>

                <Typography variant="body2" sx={{ mt: 1.1 }} color="text.secondary">
                  Submitted by:{' '}
                  <Box component="span" sx={{ fontWeight: 900, color: 'text.primary' }}>
                    {view.submittedBy}
                  </Box>
                </Typography>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  className="avoid-break"
                  sx={{
                    p: 1.75,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    background: BRAND_SOFT_BG,
                  }}
                >
                  <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.left, opacity: 0.35, pointerEvents: 'none' }} />
                  <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.right, opacity: 0.35, pointerEvents: 'none' }} />

                  <Box sx={{ position: 'relative' }}>
                    <Typography variant="caption" color="text.secondary">
                      Shop cash loss / up (day)
                    </Typography>

                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 1000,
                          letterSpacing: -0.4,
                          color:
                            view.shopVariance > 0
                              ? 'success.main'
                              : view.shopVariance < 0
                              ? 'error.main'
                              : 'text.primary',
                        }}
                      >
                        {gbp(view.shopVariance)}
                      </Typography>
                      <VariancePill value={view.shopVariance} />
                    </Stack>

                    <Divider sx={{ my: 1.25 }} />

                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        Exceptions total: <b>{gbp(totals.totalEx)}</b>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Drive-offs: <b>{gbp(totals.totalDriveOff)}</b> ({totals.totalDriveOffQty.toLocaleString()}) · NMOP:{' '}
                        <b>{gbp(totals.totalNmop)}</b> ({totals.totalNmopQty.toLocaleString()})
                      </Typography>

                      <Chip
                        size="small"
                        color={varianceSeverity === 'warning' ? 'warning' : varianceSeverity === 'success' ? 'success' : 'info'}
                        variant="outlined"
                        sx={{ mt: 0.75, borderRadius: 999, width: 'fit-content', fontWeight: 900 }}
                        label={
                          varianceSeverity === 'warning'
                            ? 'Attention: variance is high'
                            : varianceSeverity === 'success'
                            ? 'Variance is clean'
                            : 'Variance within normal range'
                        }
                      />
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Smart interpretation */}
          <Paper
            variant="outlined"
            className="avoid-break"
            sx={{ ...cardSx, p: { xs: 1.75, sm: 2 }, bgcolor: '#f8fcf9' }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <BrandIconBox sx={{ width: 38, height: 38 }}><InfoIcon /></BrandIconBox>
              <Box>
                <Typography sx={{ fontWeight: 1000 }}>Report at a glance</Typography>
                <Typography variant="caption" color="text.secondary">The most important conclusions from this submission.</Typography>
              </Box>
            </Stack>
            <Grid container spacing={1.25}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%', borderColor: cashAccounted >= 99 ? '#b7dec2' : '#f0cf9b' }}>
                  <Typography variant="caption" color="text.secondary">Cash accounted for</Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 1000 }}>{cashAccounted.toFixed(1)}%</Typography>
                  <Typography variant="body2">{cashAccounted >= 99 ? 'Recorded pouch and safe-drop values closely match expected cash.' : 'Review pouch, safe-drop and paid-in/out entries.'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
                  <Typography variant="caption" color="text.secondary">Customer mix</Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 1000 }}>{metrics.fuelOnlyPct.toFixed(1)}% fuel transactions</Typography>
                  <Typography variant="body2">{Number(metrics.totalCustomers || 0).toLocaleString()} customers and {Number(metrics.fuelTransactions || 0).toLocaleString()} fuel transactions were recorded.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%', borderColor: varianceSeverity === 'warning' ? '#f0cf9b' : '#b7dec2' }}>
                  <Typography variant="caption" color="text.secondary">Control check</Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 1000 }}>{varianceSeverity === 'warning' ? 'Review required' : 'Within control'}</Typography>
                  <Typography variant="body2">Cash variance {gbp(view.shopVariance)} · Costa cup variance {costaVariance > 0 ? '+' : ''}{costaVariance}.</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Cash summary */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryKpi label="Expected cash (all tills)" value={gbp(totals.totalCash)} hint="Shop tills only" accent />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryKpi label="Pouch amounts" value={gbp(totals.totalPouch)} hint="Shop tills only" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryKpi label="Safe drop / submitted cash" value={gbp(totals.totalSafeDrop)} hint="Shop tills only" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryKpi label="Manual paid in/out" value={gbp(totals.totalManual)} hint="Shop tills only" accent />
            </Grid>
            {/* Shown on the record that carries the week, even at zero: a
                blank jetwash line on a Sunday is the tell that nobody
                entered it. */}
            {view.isWeeklyEntryDay ? (
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi
                  label="Machines cash"
                  value={gbp(view.jetwash)}
                  hint="Banked on the Monday"
                  accent
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={3}>
              <SummaryKpi
                label="Total cash to bank"
                value={gbp(view.cashToBank)}
                hint="Pouch + safe drop + paid in/out"
                accent
              />
            </Grid>
          </Grid>

          {/* Daily summary */}
          <Paper
            variant="outlined"
            className="avoid-break"
            sx={{
              ...cardSx,
              p: { xs: 2, sm: 2.25 },
              position: 'relative',
              background: (t) => BRAND_PANEL_BG(t),
            }}
          >
            <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.left, opacity: 0.18, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.right, opacity: 0.18, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', left: 0, top: 0, right: 0, height: 4, background: BRAND_GRADIENT }} />

            <Typography variant="h6" sx={{ fontWeight: 1000, mb: 1.5, position: 'relative' }}>
              Daily summary
            </Typography>

            <Grid container spacing={1.5} sx={{ position: 'relative' }}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Wastage" value={gbp(metrics.wastage)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Markdown" value={gbp(metrics.markdown)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Daily float" value={gbp(metrics.dailyFloat)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi
                  label="Weekly / backup float"
                  value={gbp(metrics.sundayFloat)}
                  hint={view.isFloatCountDay ? 'Counted this morning' : 'Counted on a Sunday, against Saturday'}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Costa cups dispensed" value={Number(metrics.costaDispensed || 0).toLocaleString()} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Costa cups sold" value={Number(metrics.costaSold || 0).toLocaleString()} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Total customers" value={Number(metrics.totalCustomers || 0).toLocaleString()} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryKpi label="Fuel transactions" value={Number(metrics.fuelTransactions || 0).toLocaleString()} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <SummaryKpi label="Night shift fuel" value={`${toNum(metrics.nightFuel).toFixed(2)} L`} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SummaryKpi label="Night shift shop sales" value={gbp(metrics.nightShopSales)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SummaryKpi label="Night shift customers" value={Number(metrics.nightCustomers || 0).toLocaleString()} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <SummaryKpi label="Solar generated" value={`${Number(metrics.solarKwh || 0).toLocaleString()} kWh`} />
              </Grid>
            </Grid>
          </Paper>

          {/* Subway section */}
          {view.isMoortown && view.subway ? (
            <Paper
              variant="outlined"
              className="avoid-break"
              sx={{
                ...cardSx,
                p: { xs: 2, sm: 2.25 },
                position: 'relative',
                background: (t) => SUBWAY_PANEL_BG(t),
                boxShadow: 0,
                borderColor: alpha(SUBWAY.green, 0.18),
              }}
            >
              <Box sx={{ position: 'absolute', left: 0, top: 0, right: 0, height: 4, background: SUBWAY_GRADIENT }} />
              <Box sx={{ position: 'absolute', inset: -2, background: subwayGlow.left, opacity: 0.22, pointerEvents: 'none' }} />
              <Box sx={{ position: 'absolute', inset: -2, background: subwayGlow.right, opacity: 0.22, pointerEvents: 'none' }} />

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1.25, position: 'relative' }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>
                    Subway (Moortown) — Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Moortown only · Subway is recorded as a single day shift.
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  sx={{
                    height: 28,
                    borderRadius: 999,
                    fontWeight: 900,
                    bgcolor: alpha(theme.palette.common.white, 0.75),
                    borderColor: alpha(SUBWAY.green, 0.35),
                  }}
                  variant="outlined"
                  label={`Cash variance: ${gbp(view.subway.totals.cashVariance)}`}
                />
              </Stack>

              <Divider sx={{ mb: 1.5, position: 'relative' }} />

              <Grid container spacing={1.5} sx={{ position: 'relative' }}>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Total sales" value={gbp(view.subway.totals.totalSales)} hint="Subway day total" subway />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Total customers" value={Number(view.subway.totals.totalCustomers || 0).toLocaleString()} hint="Subway day total" subway />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Night sales" value={gbp(view.subway.totals.nightSales)} hint="11pm–7am" subway />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Night customers" value={Number(view.subway.totals.nightCustomers || 0).toLocaleString()} hint="11pm–7am" subway />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Weekly wastage" value={gbp(view.subway.totals.weeklyWastage)} hint="Weekly wastage figure" subway />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Total cash (before float)" value={gbp(view.subway.totals.totalCashBeforeFloat)} hint="Subway expected cash" subway />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Float amount" value={gbp(view.subway.totals.floatAmount)} hint="Subway float" subway />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SummaryKpi label="Total cash (after float)" value={gbp(view.subway.totals.totalCashAfterFloat)} hint="Before float − float" subway />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <SummaryKpi label="Pouch amount" value={gbp(view.subway.totals.pouchTotal)} hint="Day total" subway />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SummaryKpi label="Cash safe drop" value={gbp(view.subway.totals.safeDropTotal)} hint="Day total" subway />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SummaryKpi label="Manual paid in/out" value={gbp(view.subway.totals.manualTotal)} hint="Day total" subway />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.75, position: 'relative' }} />

              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  borderRadius: 3,
                  position: 'relative',
                  background: (t) => SUBWAY_PANEL_BG(t),
                  borderColor: alpha(SUBWAY.green, 0.18),
                }}
              >
                <Box sx={{ position: 'absolute', left: 0, top: 0, right: 0, height: 4, background: SUBWAY_GRADIENT }} />
                <Box sx={{ position: 'absolute', inset: -2, background: subwayGlow.left, opacity: 0.12, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', inset: -2, background: subwayGlow.right, opacity: 0.12, pointerEvents: 'none' }} />

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  sx={{ position: 'relative' }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 1000, fontSize: 17 }}>
                      {view.subway.shift.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Staff on duty
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 900, mt: 0.25 }}>
                      {view.subway.shift.staffText || 'No staff recorded'}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                    <Chip
                      size="small"
                      label={`Pouch: ${gbp(view.subway.shift.pouch)}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        bgcolor: alpha(theme.palette.common.white, 0.74),
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Safe drop: ${gbp(view.subway.shift.safeDrop)}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        bgcolor: alpha(theme.palette.common.white, 0.74),
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Manual: ${gbp(view.subway.shift.manual)}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        bgcolor: alpha(theme.palette.common.white, 0.74),
                      }}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, position: 'relative' }}>
                Subway values shown here are taken from the Moortown Subway section in the reconciliation pack.
              </Typography>
            </Paper>
          ) : null}

          {/* Till overview — on screen only: the printed table below
              carries the same figures with a subtotal per till. */}
          <Box className="keep-with-next">
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.25 }}>
              <Typography variant="h6" sx={{ fontWeight: 1000 }}>
                Till overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Shop tills only
              </Typography>
            </Stack>

            <Grid container spacing={1.5}>
              {TILLS.map((t) => {
                const r = tillRollup[t];
                const exLabel = `DO ${gbp(r.dr)} (${r.drQty.toLocaleString()}) · NMOP ${gbp(r.nm)} (${r.nmQty.toLocaleString()})`;

                return (
                  <Grid key={t} item xs={12} md={4}>
                    <Paper
                      variant="outlined"
                      className="avoid-break"
                      sx={{
                        ...cardSx,
                        p: 1.75,
                        background: (tt) => BRAND_PANEL_BG(tt),
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.left, opacity: 0.25, pointerEvents: 'none' }} />
                      <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.right, opacity: 0.25, pointerEvents: 'none' }} />
                      <Box sx={{ position: 'absolute', left: 0, top: 0, right: 0, height: 4, background: BRAND_GRADIENT }} />

                      <Box sx={{ position: 'relative' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: 1000 }}>{TILL_LABEL[t]}</Typography>
                          <VariancePill value={r.var} />
                        </Stack>

                        <Divider sx={{ mb: 1.25 }} />

                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Field label="Total cash" value={gbp(r.cash)} />
                            <Field label="Pouch" value={gbp(r.pouch)} />
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Field label="Safe drop" value={gbp(r.safe)} />
                            <Field label="Manual" value={gbp(r.manual)} />
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Field label="Exceptions" value={gbp(r.ex)} emphasize={Math.abs(r.ex) >= 50} />
                            <Field label="Till variance" value={gbp(r.var)} />
                          </Stack>

                          <Typography variant="caption" color="text.secondary">
                            {exLabel}
                          </Typography>
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Shift detail */}
          <Paper
            variant="outlined"
            className="avoid-break"
            sx={{
              ...cardSx,
              p: { xs: 2, sm: 2.25 },
              background: (t) => BRAND_PANEL_BG(t),
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.left, opacity: 0.18, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', inset: -2, background: brandGlow.right, opacity: 0.18, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', left: 0, top: 0, right: 0, height: 4, background: BRAND_GRADIENT }} />

            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.25, position: 'relative' }}>
              <Typography variant="h6" sx={{ fontWeight: 1000 }}>
                Till shifts (detail)
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <InfoIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" className="print-tip">
                  Tip: You can print / save as PDF from the header.
                </Typography>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 1.5, position: 'relative' }} />

            <Grid container spacing={1.5} sx={{ position: 'relative' }}>
              {view.perShift.map((s, idx) => (
                <Grid key={`${s.till}-${s.shift}-${idx}`} item xs={12} md={6}>
                  <Paper variant="outlined" className="avoid-break" sx={{ p: 1.5, borderRadius: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontWeight: 1000 }}>{s.label}</Typography>
                      <VariancePill value={s.variance} />
                    </Stack>

                    <Divider sx={{ mb: 1 }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Field label="Total cash" value={gbp(s.cash)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Field label="Pouch" value={gbp(s.pouch)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Field label="Safe drop" value={gbp(s.safe)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Field label="Manual" value={gbp(s.manual)} />
                      </Grid>
                      <Grid item xs={12}>
                        <Field label="Exceptions" value={gbp(s.exTotal)} emphasize={Math.abs(s.exTotal) >= 50} />
                      </Grid>
                    </Grid>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      DO {gbp(s.drAmt)} ({s.drQty.toLocaleString()}) · NMOP {gbp(s.nmAmt)} ({s.nmQty.toLocaleString()})
                    </Typography>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Staff: <b>{s.staff}</b>
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/*
            What this document is, on every sheet.

            The browser prints its own header - a URL and a timestamp - and
            that cannot be turned off from here, but it says nothing about
            the site or the day. This does, and it repeats: a fixed element
            is drawn on each printed page.
          */}
        </Box>

        <Box className="print-only print-sheet">
          <PrintReport view={view} totals={totals} />

          <Box className="print-footer" sx={{ display: "none" }}>
            {view.siteName} · {niceDate(view.dateObj)} · Daily Banking &amp; Reconciliation
            {view.submittedBy ? ` · submitted by ${view.submittedBy}` : ""}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="no-print" sx={{ px: 2.25, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 999, textTransform: 'none' }}>
          Close
        </Button>
        <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />} sx={{ borderRadius: 999, textTransform: 'none' }}>
          Print / Save PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
